import { useState } from "react";
import { useQuery } from "@apollo/client/react";
import { ChevronDown, ListChecks } from "lucide-react";
import {
    Button,
    Checkbox,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { OrganizationOptionsDocument } from "@/graphql/organizations.generated";
import { flattenOrgOptions } from "@/lib/organizationOptions";

/** Indentation per nesting level, as in OrganizationSelect: radio items carry `pl-8` for their dot. */
const INDENT_CLASSES = [
    "",
    "pl-12",
    "pl-16",
    "pl-20",
] as const;

/** The same steps for the checkbox rows, which start at the plain item's `px-2`. */
const CHECKBOX_INDENT_CLASSES = [
    "",
    "pl-6",
    "pl-10",
    "pl-14",
] as const;

/** The radio value for "All organizations". Never an organization id. */
const ALL = "__all_organizations__";

interface OrganizationScopeSelectProps {
    value: string[];
    onValueChange: (value: string[]) => void;
    /** What the closed control says about the selection; the caller decides how to name it. */
    label?: string;
    /** Offer "All organizations" while choosing one at a time. */
    offerAll?: boolean;
    /** Shown when nothing is selected. */
    placeholder?: string;
    className?: string;
    "aria-label"?: string;
}

/**
 * The organization picker for reading one, several or all organizations at once (ATH-247).
 *
 * One at a time by default, picked like any dropdown. The checklist button to its left switches to
 * several and opens the list: the rows become checkboxes, with "All" to select or clear every one,
 * and the list stays open while they are checked. The button stays filled while several are being
 * chosen; pressing it again goes back to one. Nested and ordered as OrganizationSelect nests them.
 */
export function OrganizationScopeSelect({
    value,
    onValueChange,
    label,
    offerAll = false,
    placeholder = "Select an organization",
    className,
    "aria-label": ariaLabel,
}: OrganizationScopeSelectProps) {
    const { data } = useQuery(OrganizationOptionsDocument);
    const [
        multiple,
        setMultiple,
    ] = useState<boolean>(false);
    const [
        open,
        setOpen,
    ] = useState<boolean>(false);
    const options = flattenOrgOptions(data?.organizations ?? []);
    const allIds = options.map(({ org }) => org.id);
    const selected = new Set(value);
    const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
    // What one-at-a-time mode shows as chosen: one organization, or All where it is offered.
    const single = value.length === 1 ? value[0] : allSelected && offerAll ? ALL : undefined;

    /** Back to one at a time keeps a selection the list can show, and clears any other. */
    const switchMode = () => {
        if (multiple && single === undefined && value.length > 0) onValueChange([]);
        setMultiple(!multiple);
        // Turning it on opens the checkboxes straight away, so the switch visibly does something.
        setOpen(!multiple);
    };

    /** Everything nested under an organization, at any depth, among those the caller can pick. */
    const descendants = (id: string): string[] =>
        options
            .filter(({ org }) => org.parentOrganizationId === id)
            .flatMap(({ org }) => [
                org.id,
                ...descendants(org.id),
            ]);

    /**
     * Checks or unchecks one organization together with its sub-organizations, at every depth, so a
     * university and its campuses move as one: dropping it drops them, and checking it again brings
     * them back. A sub-organization can still be unchecked on its own afterwards.
     * Kept in list order, so whatever reads the selection meets the organizations as they are shown.
     */
    const toggle = (id: string) => {
        const checking = !selected.has(id);
        const branch = new Set([
            id,
            ...descendants(id),
        ]);
        onValueChange(allIds.filter((candidate) => (branch.has(candidate) ? checking : selected.has(candidate))));
    };

    const indent = (depth: number, classes: readonly string[] = INDENT_CLASSES) =>
        classes[Math.min(depth, classes.length - 1)];

    /**
     * A row with a visible box, checked or not, so several-at-a-time reads as checkboxes the moment
     * it opens. The menu's own checkbox item draws only a tick when checked, which left the rows
     * looking exactly like the one-at-a-time list. Roles as Radix gives its checkbox item.
     */
    const checkboxRow = (
        key: string,
        checked: boolean | "indeterminate",
        onToggle: () => void,
        text: string,
        className?: string,
    ) => (
        <DropdownMenuItem
            key={key}
            role="menuitemcheckbox"
            aria-checked={checked === "indeterminate" ? "mixed" : checked}
            onSelect={(event) => {
                event.preventDefault();
                onToggle();
            }}
            className={className}
        >
            <Checkbox checked={checked} tabIndex={-1} aria-hidden className="pointer-events-none" />
            {text}
        </DropdownMenuItem>
    );
    const canSelectMultiple = allIds.length > 1;

    return (
        <div className={cn("flex items-center gap-2", className)}>
            {canSelectMultiple && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant={multiple ? "default" : "outline"}
                                size="icon"
                                aria-label="Select multiple"
                                aria-pressed={multiple}
                                onClick={switchMode}
                            >
                                <ListChecks className="h-4 w-4" aria-hidden />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{multiple ? "Back to one organization" : "Select multiple"}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
            {/* Not modal, so the page behind stays readable while organizations are checked. */}
            <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="outline"
                        className="min-w-0 flex-1 justify-between font-normal"
                        aria-label={ariaLabel}
                    >
                        <span className="truncate">{label ?? placeholder}</span>
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="start"
                    className="max-h-92 w-(--radix-dropdown-menu-trigger-width) min-w-64"
                >
                    {multiple ? (
                        <>
                            {checkboxRow(
                                ALL,
                                allSelected ? true : value.length > 0 ? "indeterminate" : false,
                                () => onValueChange(allSelected ? [] : allIds),
                                "All",
                                "font-medium",
                            )}
                            {options.map(({ org, depth }) =>
                                checkboxRow(
                                    org.id,
                                    selected.has(org.id),
                                    () => toggle(org.id),
                                    org.name,
                                    indent(depth, CHECKBOX_INDENT_CLASSES),
                                ),
                            )}
                        </>
                    ) : (
                        <DropdownMenuRadioGroup
                            value={single ?? ""}
                            onValueChange={(next) => onValueChange(next === ALL ? allIds : [next])}
                        >
                            {offerAll && <DropdownMenuRadioItem value={ALL}>All organizations</DropdownMenuRadioItem>}
                            {options.map(({ org, depth }) => (
                                <DropdownMenuRadioItem key={org.id} value={org.id} className={indent(depth)}>
                                    {org.name}
                                </DropdownMenuRadioItem>
                            ))}
                        </DropdownMenuRadioGroup>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
